import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '@/store/authStore'
import { apiClient } from '@/services/api'
import toast from 'react-hot-toast'
import {
  User,
  Building2,
  Save,
  Key,
  Plus,
  Trash2,
  Copy,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
  AlertTriangle,
  Pencil,
  Bell,
  Plug,
  Share2,
  Link2,
  Send,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Lock,
  Workflow,
  // ExternalLink is available for future webhook features
} from 'lucide-react'
import { notificationsService } from '@/services/notifications.service'
import { referralService } from '@/services/referral.service'
import type { ReferralCodeInfo } from '@/services/referral.service'
import type { NotificationChannelConfig, NotificationChannelType } from '@/types'
import { webhooksService } from '@/services/webhooks.service'
import type { OrgWebhook, WebhookDeliveryLog, OrgWebhookEventType } from '@/types'
import { nikaidoService } from '@/services/nikaido.service'
import type { NikaidoConnectionStatus } from '@/types'

interface ApiToken {
  id: string
  name: string
  token_prefix: string
  permissions: Record<string, string[]>
  is_active: boolean
  last_used_at?: string
  expires_at?: string
  created_at: string
}

const getTabs = () => [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'organization', label: 'Organization', icon: Building2 },
  { id: 'api-tokens', label: 'API Tokens', icon: Key },
  { id: 'notification-channels', label: 'Notifications', icon: Bell },
  { id: 'mcp', label: 'MCP Server', icon: Plug },
  { id: 'referrals', label: 'Referrals', icon: Share2 },
  { id: 'webhooks', label: 'Webhooks', icon: Link2 },
  { id: 'nikaido', label: 'Nikaido', icon: Workflow },
  { id: 'security', label: 'Security', icon: Lock },
]

export function SettingsPage() {
  const { user, organization } = useAuthStore()
  const [activeTab, setActiveTab] = useState('profile')
  const tabs = getTabs()

  // Profile edit state
  const [profileName, setProfileName] = useState(user?.name || '')
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [isEditingProfile, setIsEditingProfile] = useState(false)

  // Organization edit state
  const [orgName, setOrgName] = useState(organization?.name || '')
  const [isSavingOrg, setIsSavingOrg] = useState(false)
  const [isEditingOrg, setIsEditingOrg] = useState(false)

  // Sync state when store updates
  useEffect(() => {
    setProfileName(user?.name || '')
  }, [user?.name])

  useEffect(() => {
    setOrgName(organization?.name || '')
  }, [organization?.name])

  const handleSaveProfile = async () => {
    if (!profileName.trim()) {
      toast.error('Name is required')
      return
    }
    setIsSavingProfile(true)
    try {
      await apiClient.put('/auth/profile', { name: profileName.trim() })
      useAuthStore.getState().setUser({ ...user!, name: profileName.trim() })
      toast.success('Profile updated')
      setIsEditingProfile(false)
    } catch {
      // error toast handled by interceptor
    } finally {
      setIsSavingProfile(false)
    }
  }

  const handleSaveOrg = async () => {
    if (!orgName.trim()) {
      toast.error('Organization name is required')
      return
    }
    setIsSavingOrg(true)
    try {
      await apiClient.put('/settings/organization', { name: orgName.trim() })
      useAuthStore.getState().setOrganization({ ...organization!, name: orgName.trim() })
      toast.success('Organization updated')
      setIsEditingOrg(false)
    } catch {
      // error toast handled by interceptor
    } finally {
      setIsSavingOrg(false)
    }
  }

  // API Tokens state
  const [tokens, setTokens] = useState<ApiToken[]>([])
  const [isLoadingTokens, setIsLoadingTokens] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newTokenName, setNewTokenName] = useState('')
  const [newTokenPermissions, setNewTokenPermissions] = useState<Record<string, string[]>>({
    integrations: ['read', 'write'],
    items: ['read', 'write'],
  })
  const [newTokenExpiry, setNewTokenExpiry] = useState('')
  const [newTokenResult, setNewTokenResult] = useState<{ token: string; name: string } | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [tokenCopied, setTokenCopied] = useState(false)
  const [editingToken, setEditingToken] = useState<ApiToken | null>(null)
  const [editTokenPermissions, setEditTokenPermissions] = useState<Record<string, string[]>>({})
  const [isSavingPermissions, setIsSavingPermissions] = useState(false)

  // Notification channels state
  const [notifChannels, setNotifChannels] = useState<NotificationChannelConfig[]>([])
  const [isLoadingChannels, setIsLoadingChannels] = useState(false)
  const [showCreateChannelModal, setShowCreateChannelModal] = useState(false)
  const [editingChannel, setEditingChannel] = useState<NotificationChannelConfig | null>(null)
  const [channelForm, setChannelForm] = useState({ name: '', type: 'webhook' as string, config: {} as Record<string, unknown>, is_enabled: true })
  const [isSavingChannel, setIsSavingChannel] = useState(false)
  const [testingChannelId, setTestingChannelId] = useState<string | null>(null)

  // Referral state
  const [referralCodes, setReferralCodes] = useState<ReferralCodeInfo[]>([])
  const [referralLoading, setReferralLoading] = useState(false)

  // Webhooks state
  const [webhooks, setWebhooks] = useState<OrgWebhook[]>([])
  const [isLoadingWebhooks, setIsLoadingWebhooks] = useState(false)
  const [showCreateWebhookModal, setShowCreateWebhookModal] = useState(false)
  const [editingWebhook, setEditingWebhook] = useState<OrgWebhook | null>(null)
  const [webhookUrl, setWebhookUrl] = useState('')
  const [webhookDescription, setWebhookDescription] = useState('')
  const [webhookEvents, setWebhookEvents] = useState<OrgWebhookEventType[]>([])
  const [isSavingWebhook, setIsSavingWebhook] = useState(false)
  const [createdWebhookSecret, setCreatedWebhookSecret] = useState<string | null>(null)
  const [testingWebhookId, setTestingWebhookId] = useState<string | null>(null)
  const [expandedWebhookLogs, setExpandedWebhookLogs] = useState<string | null>(null)
  const [webhookLogs, setWebhookLogs] = useState<Record<string, WebhookDeliveryLog[]>>({})
  const [isLoadingLogs, setIsLoadingLogs] = useState(false)

  // Nikaido state
  const [nikaidoStatus, setNikaidoStatus] = useState<NikaidoConnectionStatus | null>(null)
  const [isLoadingNikaido, setIsLoadingNikaido] = useState(false)
  const [isProvisioningNikaido, setIsProvisioningNikaido] = useState(false)
  const [isSavingNikaido, setIsSavingNikaido] = useState(false)
  const [showManualNikaido, setShowManualNikaido] = useState(false)
  const [nikaidoToken, setNikaidoToken] = useState('')
  const [nikaidoApiBaseUrl, setNikaidoApiBaseUrl] = useState('')
  const [nikaidoWebBaseUrl, setNikaidoWebBaseUrl] = useState('')

  // Security / Password change state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error('Please enter your current password')
      return
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match')
      return
    }
    setIsChangingPassword(true)
    try {
      await apiClient.post('/auth/password/change', {
        current_password: currentPassword,
        new_password: newPassword,
      })
      toast.success('Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      if (error.response?.data?.error) {
        toast.error(error.response.data.error)
      }
    } finally {
      setIsChangingPassword(false)
    }
  }

  const WEBHOOK_EVENT_OPTIONS: { value: OrgWebhookEventType; label: string; description: string }[] = [
    { value: 'conversation.created', label: 'Conversation Created', description: 'New lead starts a chat' },
    { value: 'conversation.resolved', label: 'Conversation Resolved', description: 'Chat is closed/resolved' },
    { value: 'conversation.escalated', label: 'Conversation Escalated', description: 'Agent escalated to human' },
    { value: 'message.received', label: 'Message Received', description: 'Customer sent a message' },
    { value: 'message.responded', label: 'Message Responded', description: 'AI agent replied' },
    { value: 'data.collected', label: 'Data Collected', description: 'Customer data fields captured' },
    { value: 'attachment.received', label: 'Attachment Received', description: 'Customer uploaded a file' },
    { value: 'contact.created', label: 'Contact Created', description: 'New contact/lead captured' },
  ]

  const fetchTokens = useCallback(async () => {
    setIsLoadingTokens(true)
    try {
      const res = await apiClient.get('/tokens')
      setTokens(res.data.data || [])
    } catch {
      // error toast handled by interceptor
    } finally {
      setIsLoadingTokens(false)
    }
  }, [])

  const fetchNotifChannels = useCallback(async () => {
    setIsLoadingChannels(true)
    try {
      const channels = await notificationsService.getChannels()
      setNotifChannels(channels)
    } catch {
      // error toast handled by interceptor
    } finally {
      setIsLoadingChannels(false)
    }
  }, [])

  const fetchReferralCodes = useCallback(async () => {
    setReferralLoading(true)
    try {
      const codes = await referralService.getMyCodes()
      setReferralCodes(codes)
    } catch {
      // ignore
    } finally {
      setReferralLoading(false)
    }
  }, [])

  const fetchWebhooks = useCallback(async () => {
    setIsLoadingWebhooks(true)
    try {
      const data = await webhooksService.list()
      setWebhooks(data)
    } catch {
      toast.error('Failed to load webhooks')
    } finally {
      setIsLoadingWebhooks(false)
    }
  }, [])

  const fetchNikaidoStatus = useCallback(async () => {
    setIsLoadingNikaido(true)
    try {
      const status = await nikaidoService.getStatus()
      setNikaidoStatus(status)
      setNikaidoApiBaseUrl(status.api_base_url || '')
      setNikaidoWebBaseUrl(status.web_base_url || '')
    } catch {
      toast.error('Failed to load Nikaido connection')
    } finally {
      setIsLoadingNikaido(false)
    }
  }, [])

  const handleProvisionNikaido = async () => {
    setIsProvisioningNikaido(true)
    try {
      const status = await nikaidoService.provision()
      setNikaidoStatus(status)
      setNikaidoApiBaseUrl(status.api_base_url || '')
      setNikaidoWebBaseUrl(status.web_base_url || '')
      toast.success('Nikaido connection created')
    } catch {
      toast.error('Failed to create Nikaido connection')
    } finally {
      setIsProvisioningNikaido(false)
    }
  }

  const handleConnectNikaido = async () => {
    if (!nikaidoToken.trim()) {
      toast.error('Nikaido token is required')
      return
    }
    setIsSavingNikaido(true)
    try {
      const status = await nikaidoService.connect({
        token: nikaidoToken.trim(),
        api_base_url: nikaidoApiBaseUrl.trim() || undefined,
        web_base_url: nikaidoWebBaseUrl.trim() || undefined,
      })
      setNikaidoStatus(status)
      setNikaidoToken('')
      setShowManualNikaido(false)
      toast.success('Nikaido token connected')
    } catch {
      toast.error('Failed to connect Nikaido token')
    } finally {
      setIsSavingNikaido(false)
    }
  }

  const handleDisconnectNikaido = async () => {
    if (!window.confirm('Disconnect Nikaido from this organization?')) return
    setIsSavingNikaido(true)
    try {
      const status = await nikaidoService.disconnect()
      setNikaidoStatus(status)
      setNikaidoToken('')
      toast.success('Nikaido disconnected')
    } catch {
      toast.error('Failed to disconnect Nikaido')
    } finally {
      setIsSavingNikaido(false)
    }
  }

  const resetWebhookForm = () => {
    setWebhookUrl('')
    setWebhookDescription('')
    setWebhookEvents([])
    setEditingWebhook(null)
    setCreatedWebhookSecret(null)
  }

  const handleCreateWebhook = async () => {
    if (!webhookUrl.trim()) { toast.error('URL is required'); return }
    if (webhookEvents.length === 0) { toast.error('Select at least one event'); return }
    setIsSavingWebhook(true)
    try {
      const webhook = await webhooksService.create({
        url: webhookUrl.trim(),
        description: webhookDescription.trim() || undefined,
        events: webhookEvents,
      })
      setCreatedWebhookSecret(webhook.secret || null)
      await fetchWebhooks()
      toast.success('Webhook created')
    } catch {
      toast.error('Failed to create webhook')
    } finally {
      setIsSavingWebhook(false)
    }
  }

  const handleUpdateWebhook = async () => {
    if (!editingWebhook) return
    if (!webhookUrl.trim()) { toast.error('URL is required'); return }
    if (webhookEvents.length === 0) { toast.error('Select at least one event'); return }
    setIsSavingWebhook(true)
    try {
      await webhooksService.update(editingWebhook.id, {
        url: webhookUrl.trim(),
        description: webhookDescription.trim(),
        events: webhookEvents,
      })
      setShowCreateWebhookModal(false)
      resetWebhookForm()
      await fetchWebhooks()
      toast.success('Webhook updated')
    } catch {
      toast.error('Failed to update webhook')
    } finally {
      setIsSavingWebhook(false)
    }
  }

  const handleDeleteWebhook = async (id: string) => {
    if (!confirm('Delete this webhook? This cannot be undone.')) return
    try {
      await webhooksService.remove(id)
      await fetchWebhooks()
      toast.success('Webhook deleted')
    } catch {
      toast.error('Failed to delete webhook')
    }
  }

  const handleToggleWebhook = async (webhook: OrgWebhook) => {
    try {
      await webhooksService.update(webhook.id, { is_active: !webhook.is_active })
      await fetchWebhooks()
      toast.success(webhook.is_active ? 'Webhook disabled' : 'Webhook enabled')
    } catch {
      toast.error('Failed to toggle webhook')
    }
  }

  const handleTestWebhook = async (id: string) => {
    setTestingWebhookId(id)
    try {
      const result = await webhooksService.test(id)
      if (result.success) {
        toast.success(`Test event sent (HTTP ${result.status})`)
      } else {
        toast.error(`Test failed: ${result.error || `HTTP ${result.status}`}`)
      }
    } catch {
      toast.error('Failed to send test event')
    } finally {
      setTestingWebhookId(null)
    }
  }

  const handleViewLogs = async (webhookId: string) => {
    if (expandedWebhookLogs === webhookId) {
      setExpandedWebhookLogs(null)
      return
    }
    setExpandedWebhookLogs(webhookId)
    setIsLoadingLogs(true)
    try {
      const result = await webhooksService.getDeliveryLogs(webhookId)
      setWebhookLogs(prev => ({ ...prev, [webhookId]: result.logs }))
    } catch {
      toast.error('Failed to load delivery logs')
    } finally {
      setIsLoadingLogs(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'api-tokens') {
      fetchTokens()
    }
    if (activeTab === 'notification-channels') {
      fetchNotifChannels()
    }
    if (activeTab === 'referrals') {
      fetchReferralCodes()
    }
    if (activeTab === 'webhooks') {
      fetchWebhooks()
    }
    if (activeTab === 'nikaido') {
      fetchNikaidoStatus()
    }
  }, [activeTab, fetchTokens, fetchNotifChannels, fetchReferralCodes, fetchWebhooks, fetchNikaidoStatus])

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const handleCreateToken = async () => {
    if (!newTokenName.trim()) {
      toast.error('Please enter a token name')
      return
    }
    setIsCreating(true)
    try {
      const res = await apiClient.post('/tokens', {
        name: newTokenName.trim(),
        permissions: newTokenPermissions,
        expires_at: newTokenExpiry || undefined,
      })
      setNewTokenResult({ token: res.data.data.token, name: res.data.data.name })
      setShowCreateModal(false)
      setNewTokenName('')
      setNewTokenPermissions({ agents: ['read'], conversations: ['read'], knowledge_bases: ['read'], analytics: ['read'] })
      setNewTokenExpiry('')
      fetchTokens()
      toast.success('API token created')
    } catch {
      // error toast handled by interceptor
    } finally {
      setIsCreating(false)
    }
  }

  const handleRevokeToken = async (id: string, name: string) => {
    if (!window.confirm(`Revoke token "${name}"? This cannot be undone.`)) return
    try {
      await apiClient.delete(`/tokens/${id}`)
      setTokens((prev) => prev.filter((t) => t.id !== id))
      toast.success('Token revoked')
    } catch {
      // error toast handled by interceptor
    }
  }

  const handleSavePermissions = async () => {
    if (!editingToken) return
    setIsSavingPermissions(true)
    try {
      await apiClient.put(`/tokens/${editingToken.id}/permissions`, { permissions: editTokenPermissions })
      setTokens((prev) => prev.map((t) => t.id === editingToken.id ? { ...t, permissions: editTokenPermissions } : t))
      setEditingToken(null)
      toast.success('Permissions updated')
    } catch {
      toast.error('Failed to update permissions')
    } finally {
      setIsSavingPermissions(false)
    }
  }

  const toggleEditPermission = (resource: string, action: string) => {
    setEditTokenPermissions((prev) => {
      const current = prev[resource] || []
      if (current.includes(action)) {
        return { ...prev, [resource]: current.filter((a) => a !== action) }
      }
      return { ...prev, [resource]: [...current, action] }
    })
  }

  const handleCopyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token)
      setTokenCopied(true)
      toast.success('Token copied to clipboard')
      setTimeout(() => setTokenCopied(false), 2000)
    } catch {
      toast.error('Failed to copy token')
    }
  }

  const togglePermission = (resource: string, action: string) => {
    setNewTokenPermissions((prev) => {
      const current = prev[resource] || []
      if (current.includes(action)) {
        return { ...prev, [resource]: current.filter((a) => a !== action) }
      }
      return { ...prev, [resource]: [...current, action] }
    })
  }

  const CHANNEL_TYPE_ICONS: Record<string, string> = {
    email: '\u{1F4E7}',
    slack: '#',
    discord: '\u{1F3AE}',
    telegram: '\u{2708}\u{FE0F}',
    teams: '\u{1F465}',
    webhook: '\u{1F517}',
  }

  const CHANNEL_TYPE_OPTIONS: { value: NotificationChannelType; label: string }[] = [
    { value: 'email', label: 'Email' },
    { value: 'slack', label: 'Slack' },
    { value: 'discord', label: 'Discord' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'teams', label: 'Teams' },
    { value: 'webhook', label: 'Webhook' },
  ]

  const getChannelConfigSummary = (channel: NotificationChannelConfig): string => {
    const cfg = channel.config
    switch (channel.type) {
      case 'email':
        return cfg.from_email ? `From: ${String(cfg.from_email)}` : 'Email channel'
      case 'slack':
      case 'discord':
      case 'teams':
        return cfg.webhook_url ? `URL: ${String(cfg.webhook_url).substring(0, 30)}...` : `${channel.type} channel`
      case 'telegram':
        return cfg.chat_id ? `Chat: ${String(cfg.chat_id)}` : 'Telegram channel'
      case 'webhook':
        return cfg.url ? `${String(cfg.method || 'POST')} ${String(cfg.url).substring(0, 30)}...` : 'Webhook channel'
      default:
        return `${channel.type} channel`
    }
  }

  const resetChannelForm = () => {
    setChannelForm({ name: '', type: 'webhook', config: {}, is_enabled: true })
  }

  const openEditChannel = (channel: NotificationChannelConfig) => {
    setEditingChannel(channel)
    setChannelForm({
      name: channel.name,
      type: channel.type,
      config: { ...channel.config },
      is_enabled: channel.is_enabled,
    })
  }

  const handleCreateChannel = async () => {
    if (!channelForm.name.trim()) {
      toast.error('Please enter a channel name')
      return
    }
    setIsSavingChannel(true)
    try {
      await notificationsService.createChannel({
        name: channelForm.name.trim(),
        type: channelForm.type,
        config: channelForm.config,
        is_enabled: channelForm.is_enabled,
      })
      toast.success('Notification channel created')
      setShowCreateChannelModal(false)
      resetChannelForm()
      fetchNotifChannels()
    } catch {
      // error toast handled by interceptor
    } finally {
      setIsSavingChannel(false)
    }
  }

  const handleUpdateChannel = async () => {
    if (!editingChannel) return
    if (!channelForm.name.trim()) {
      toast.error('Please enter a channel name')
      return
    }
    setIsSavingChannel(true)
    try {
      await notificationsService.updateChannel(editingChannel.id, {
        name: channelForm.name.trim(),
        type: channelForm.type,
        config: channelForm.config,
        is_enabled: channelForm.is_enabled,
      })
      toast.success('Notification channel updated')
      setEditingChannel(null)
      resetChannelForm()
      fetchNotifChannels()
    } catch {
      // error toast handled by interceptor
    } finally {
      setIsSavingChannel(false)
    }
  }

  const handleDeleteChannel = async (id: string, name: string) => {
    if (!window.confirm(`Delete notification channel "${name}"? This cannot be undone.`)) return
    try {
      await notificationsService.deleteChannel(id)
      setNotifChannels((prev) => prev.filter((c) => c.id !== id))
      toast.success('Notification channel deleted')
    } catch {
      // error toast handled by interceptor
    }
  }

  const handleTestChannel = async (id: string) => {
    setTestingChannelId(id)
    try {
      await notificationsService.testChannel(id)
      toast.success('Test notification sent')
    } catch {
      // error toast handled by interceptor
    } finally {
      setTestingChannelId(null)
    }
  }

  const renderChannelConfigFields = () => {
    const updateConfig = (key: string, value: unknown) => {
      setChannelForm((prev) => ({ ...prev, config: { ...prev.config, [key]: value } }))
    }

    switch (channelForm.type) {
      case 'email':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Email</label>
              <input
                type="email"
                value={(channelForm.config.from_email as string) || ''}
                onChange={(e) => updateConfig('from_email', e.target.value)}
                placeholder="noreply@example.com"
                className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SendGrid API Key</label>
              <input
                type="password"
                value={(channelForm.config.api_key as string) || ''}
                onChange={(e) => updateConfig('api_key', e.target.value)}
                placeholder="SG.xxxxx"
                className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none font-mono text-sm"
              />
            </div>
          </>
        )
      case 'slack':
      case 'discord':
      case 'teams':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
            <input
              type="url"
              value={(channelForm.config.webhook_url as string) || ''}
              onChange={(e) => updateConfig('webhook_url', e.target.value)}
              placeholder="https://hooks.example.com/..."
              className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none font-mono text-sm"
            />
          </div>
        )
      case 'telegram':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bot Token</label>
              <input
                type="password"
                value={(channelForm.config.bot_token as string) || ''}
                onChange={(e) => updateConfig('bot_token', e.target.value)}
                placeholder="123456:ABC-DEF..."
                className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chat ID</label>
              <input
                type="text"
                value={(channelForm.config.chat_id as string) || ''}
                onChange={(e) => updateConfig('chat_id', e.target.value)}
                placeholder="-1001234567890"
                className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none font-mono text-sm"
              />
            </div>
          </>
        )
      case 'webhook':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
              <input
                type="url"
                value={(channelForm.config.url as string) || ''}
                onChange={(e) => updateConfig('url', e.target.value)}
                placeholder="https://api.example.com/webhook"
                className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
              <select
                value={(channelForm.config.method as string) || 'POST'}
                onChange={(e) => updateConfig('method', e.target.value)}
                className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none"
              >
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Headers <span className="text-gray-400 font-normal">(optional, JSON)</span>
              </label>
              <textarea
                value={(channelForm.config.headers as string) || ''}
                onChange={(e) => updateConfig('headers', e.target.value)}
                placeholder='{"Authorization": "Bearer xxx"}'
                rows={3}
                className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none font-mono text-sm"
              />
            </div>
          </>
        )
      default:
        return null
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Settings</h3>
              <div className="flex items-center gap-6 mb-6">
                <div className="w-20 h-20 rounded-full bg-blue-500 flex items-center justify-center text-white text-2xl font-bold border-2 border-[#1a1a2e]">
                  {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                </div>
                <div>
                  <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-xl text-sm font-bold transition-all border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] hover:shadow-[3px_3px_0px_#1a1a2e] hover:translate-x-[-1px] hover:translate-y-[-1px] active:shadow-[1px_1px_0px_#1a1a2e] active:translate-x-[1px] active:translate-y-[1px]">
                    Change Photo
                  </button>
                  <p className="text-gray-500 text-sm mt-2">
                    JPG, PNG or GIF. Max 2MB.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={profileName}
                      onChange={(e) => { setProfileName(e.target.value); setIsEditingProfile(true) }}
                      className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] focus:shadow-[3px_3px_0px_#1a1a2e] outline-none transition-all"
                    />
                    {isEditingProfile && profileName !== user?.name && (
                      <button
                        onClick={handleSaveProfile}
                        disabled={isSavingProfile}
                        className="px-3 py-2 bg-blue-500 text-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] hover:shadow-[3px_3px_0px_#1a1a2e] hover:translate-x-[-1px] hover:translate-y-[-1px] active:shadow-[1px_1px_0px_#1a1a2e] active:translate-x-[1px] active:translate-y-[1px] transition-all disabled:opacity-50"
                      >
                        {isSavingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    readOnly
                    className="w-full px-3 py-2 border-2 border-[#1a1a2e]/10 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <input
                    type="text"
                    value={user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : ''}
                    readOnly
                    className="w-full px-3 py-2 border-2 border-[#1a1a2e]/10 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    value={organization?.name || ''}
                    readOnly
                    className="w-full px-3 py-2 border-2 border-[#1a1a2e]/10 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          </div>
        )

      case 'organization':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Organization Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Name
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={orgName}
                    onChange={(e) => { setOrgName(e.target.value); setIsEditingOrg(true) }}
                    className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] focus:shadow-[3px_3px_0px_#1a1a2e] outline-none transition-all"
                  />
                  {isEditingOrg && orgName !== organization?.name && (
                    <button
                      onClick={handleSaveOrg}
                      disabled={isSavingOrg}
                      className="px-3 py-2 bg-blue-500 text-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] hover:shadow-[3px_3px_0px_#1a1a2e] hover:translate-x-[-1px] hover:translate-y-[-1px] active:shadow-[1px_1px_0px_#1a1a2e] active:translate-x-[1px] active:translate-y-[1px] transition-all disabled:opacity-50"
                    >
                      {isSavingOrg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan
                </label>
                <input
                  type="text"
                  value={organization?.plan ? organization.plan.charAt(0).toUpperCase() + organization.plan.slice(1) : ''}
                  readOnly
                  className="w-full px-3 py-2 border-2 border-[#1a1a2e]/10 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>
            </div>
            {organization?.settings && (
              <div className="p-4 bg-gray-50 rounded-lg border-2 border-[#1a1a2e]">
                <h4 className="font-medium text-gray-900 mb-2">Organization Settings</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Timezone:</span>
                    <span className="text-gray-900">{organization.settings.timezone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Default Language:</span>
                    <span className="text-gray-900">{organization.settings.default_language}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Notification Email:</span>
                    <span className="text-gray-900">{organization.settings.notification_email}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )

      case 'api-tokens':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">API Tokens</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Manage API tokens for programmatic access to your organization's data.
                </p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Token
              </button>
            </div>

            {isLoadingTokens ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading tokens...</span>
              </div>
            ) : tokens.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-[#1a1a2e]/15 rounded-lg">
                <Key className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No API tokens yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  Create a token to access the API programmatically.
                </p>
              </div>
            ) : (
              <div className="border-2 border-[#1a1a2e] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-[#1a1a2e]">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Token</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Permissions</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Last Used</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Created</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-600"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tokens.map((token) => (
                      <tr key={token.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{token.name}</td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                            {token.token_prefix}
                          </code>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(token.permissions).map(([resource, actions]) =>
                              (actions as string[]).map((action) => (
                                <span
                                  key={`${resource}-${action}`}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700"
                                >
                                  {resource}:{action}
                                </span>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {token.last_used_at ? (
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {new Date(token.last_used_at).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-gray-400">Never</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {new Date(token.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => {
                                setEditingToken(token)
                                setEditTokenPermissions(token.permissions || {})
                              }}
                              className="p-1.5 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                              title="Edit permissions"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRevokeToken(token.id, token.name)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Revoke token"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Create Token Modal */}
            {showCreateModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-xl shadow-[5px_5px_0px_#1a1a2e] w-full max-w-md mx-4 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Create API Token</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Token Name</label>
                      <input
                        type="text"
                        value={newTokenName}
                        onChange={(e) => setNewTokenName(e.target.value)}
                        placeholder="e.g. Production Integration"
                        className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                        {[
                          { resource: 'agents', label: 'Agents' },
                          { resource: 'pipelines', label: 'Pipelines' },
                          { resource: 'conversations', label: 'Conversations' },
                          { resource: 'knowledge_bases', label: 'Knowledge Bases' },
                          { resource: 'channels', label: 'Channels' },
                          { resource: 'training', label: 'Training' },
                          { resource: 'skills', label: 'Skills' },
                          { resource: 'contacts', label: 'Contacts' },
                          { resource: 'documents', label: 'Documents' },
                          { resource: 'analytics', label: 'Analytics' },
                          { resource: 'team', label: 'Team' },
                          { resource: 'integrations', label: 'Integrations' },
                          { resource: 'exports', label: 'Exports' },
                          { resource: 'notifications', label: 'Notifications' },
                          { resource: 'settings', label: 'Settings' },
                          { resource: 'billing', label: 'Billing' },
                          { resource: 'credits', label: 'Credits' },
                          { resource: 'tokens', label: 'Tokens' },
                          { resource: 'webhooks', label: 'Webhooks' },
                          { resource: 'org', label: 'Organization' },
                        ].map(({ resource, label }) => (
                          <div key={resource} className="flex items-center gap-3">
                            <span className="text-xs text-gray-600 w-28 shrink-0">{label}</span>
                            {['read', 'write'].map((action) => (
                              <label key={action} className="flex items-center gap-1 text-xs cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={(newTokenPermissions[resource] || []).includes(action)}
                                  onChange={() => togglePermission(resource, action)}
                                  className="rounded border-[#1a1a2e]/15 text-violet-600 focus:ring-violet-300 w-3.5 h-3.5"
                                />
                                {action}
                              </label>
                            ))}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const all: Record<string, string[]> = {};
                          ['agents','pipelines','conversations','knowledge_bases','channels','training','skills','contacts','documents','analytics','team','integrations','exports','notifications','settings','billing','credits','tokens','webhooks','org'].forEach(r => { all[r] = ['read', 'write'] });
                          setNewTokenPermissions(all);
                        }}
                        className="mt-2 text-xs text-violet-600 hover:text-violet-800 font-medium"
                      >
                        Select all (full access)
                      </button>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expiry Date <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="date"
                        value={newTokenExpiry}
                        onChange={(e) => setNewTokenExpiry(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => {
                        setShowCreateModal(false)
                        setNewTokenName('')
                        setNewTokenExpiry('')
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateToken}
                      disabled={isCreating}
                      className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors text-sm font-medium"
                    >
                      {isCreating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Token'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Token Reveal Modal */}
            {newTokenResult && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-xl shadow-[5px_5px_0px_#1a1a2e] w-full max-w-lg mx-4 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <h3 className="text-lg font-semibold text-gray-900">Token Created</h3>
                  </div>
                  <div className="bg-amber-50 border-2 border-[#1a1a2e] rounded-lg p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-sm text-amber-800">
                        This token will only be shown once. Copy it now and store it securely.
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {newTokenResult.name}
                    </label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm bg-gray-100 px-3 py-2 rounded-lg text-gray-900 font-mono break-all select-all">
                        {newTokenResult.token}
                      </code>
                      <button
                        onClick={() => handleCopyToken(newTokenResult.token)}
                        className="shrink-0 p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Copy to clipboard"
                      >
                        {tokenCopied ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end mt-6">
                    <button
                      onClick={() => {
                        setNewTokenResult(null)
                        setTokenCopied(false)
                      }}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Permissions Modal */}
            {editingToken && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-xl shadow-[5px_5px_0px_#1a1a2e] w-full max-w-lg mx-4 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Edit Permissions</h3>
                  <p className="text-sm text-gray-500 mb-4">Token: <code className="bg-gray-100 px-1 rounded">{editingToken.name}</code></p>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-2 mb-4">
                    {[
                      { resource: 'agents', label: 'Agents' },
                      { resource: 'pipelines', label: 'Pipelines' },
                      { resource: 'conversations', label: 'Conversations' },
                      { resource: 'knowledge_bases', label: 'Knowledge Bases' },
                      { resource: 'channels', label: 'Channels' },
                      { resource: 'training', label: 'Training' },
                      { resource: 'skills', label: 'Skills' },
                      { resource: 'contacts', label: 'Contacts' },
                      { resource: 'documents', label: 'Documents' },
                      { resource: 'analytics', label: 'Analytics' },
                      { resource: 'team', label: 'Team' },
                      { resource: 'integrations', label: 'Integrations' },
                      { resource: 'exports', label: 'Exports' },
                      { resource: 'notifications', label: 'Notifications' },
                      { resource: 'settings', label: 'Settings' },
                      { resource: 'billing', label: 'Billing' },
                      { resource: 'credits', label: 'Credits' },
                      { resource: 'tokens', label: 'Tokens' },
                      { resource: 'webhooks', label: 'Webhooks' },
                      { resource: 'org', label: 'Organization' },
                    ].map(({ resource, label }) => (
                      <div key={resource} className="flex items-center gap-3">
                        <span className="text-xs text-gray-600 w-28 shrink-0">{label}</span>
                        {['read', 'write'].map((action) => (
                          <label key={action} className="flex items-center gap-1 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(editTokenPermissions[resource] || []).includes(action)}
                              onChange={() => toggleEditPermission(resource, action)}
                              className="rounded border-[#1a1a2e]/15 text-violet-600 focus:ring-violet-300 w-3.5 h-3.5"
                            />
                            {action}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const all: Record<string, string[]> = {};
                      ['agents','pipelines','conversations','knowledge_bases','channels','training','skills','contacts','documents','analytics','team','integrations','exports','notifications','settings','billing','credits','tokens','webhooks','org'].forEach(r => { all[r] = ['read', 'write'] });
                      setEditTokenPermissions(all);
                    }}
                    className="mb-4 text-xs text-violet-600 hover:text-violet-800 font-medium"
                  >
                    Select all (full access)
                  </button>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setEditingToken(null)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSavePermissions}
                      disabled={isSavingPermissions}
                      className="inline-flex items-center px-4 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 disabled:opacity-50 transition-colors text-sm font-medium"
                    >
                      {isSavingPermissions ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                      ) : 'Save Permissions'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )

      case 'notification-channels':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Notification Channels</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Configure channels to receive notifications about your organization's events.
                </p>
              </div>
              <button
                onClick={() => {
                  resetChannelForm()
                  setShowCreateChannelModal(true)
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Channel
              </button>
            </div>

            {isLoadingChannels ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading notification channels...</span>
              </div>
            ) : notifChannels.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-[#1a1a2e]/15 rounded-lg">
                <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No notification channels yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  Add a channel to start receiving notifications.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {notifChannels.map((channel) => (
                  <div
                    key={channel.id}
                    className="border-2 border-[#1a1a2e] rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 text-lg">
                          {CHANNEL_TYPE_ICONS[channel.type] || '\u{1F514}'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">{channel.name}</h4>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                              {channel.type}
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                channel.is_enabled
                                  ? 'bg-green-50 text-green-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {channel.is_enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">{getChannelConfigSummary(channel)}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditChannel(channel)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1.5" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleTestChannel(channel.id)}
                          disabled={testingChannelId === channel.id || !channel.is_enabled}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 rounded-lg transition-colors"
                        >
                          {testingChannelId === channel.id ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              Testing...
                            </>
                          ) : (
                            'Test'
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteChannel(channel.id, channel.name)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete channel"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Create/Edit Channel Modal */}
            {(showCreateChannelModal || editingChannel) && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-xl shadow-[5px_5px_0px_#1a1a2e] w-full max-w-md mx-4 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {editingChannel ? 'Edit Notification Channel' : 'Create Notification Channel'}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Channel Name</label>
                      <input
                        type="text"
                        value={channelForm.name}
                        onChange={(e) => setChannelForm((prev) => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g. Production Alerts"
                        className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Channel Type</label>
                      <select
                        value={channelForm.type}
                        onChange={(e) => setChannelForm((prev) => ({ ...prev, type: e.target.value, config: {} }))}
                        className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none"
                      >
                        {CHANNEL_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {renderChannelConfigFields()}
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={channelForm.is_enabled}
                          onChange={(e) => setChannelForm((prev) => ({ ...prev, is_enabled: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#1a1a2e]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#1a1a2e]/15 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                      <span className="text-sm font-medium text-gray-700">Enabled</span>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => {
                        setShowCreateChannelModal(false)
                        setEditingChannel(null)
                        resetChannelForm()
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={editingChannel ? handleUpdateChannel : handleCreateChannel}
                      disabled={isSavingChannel}
                      className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors text-sm font-medium"
                    >
                      {isSavingChannel ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : editingChannel ? (
                        'Update Channel'
                      ) : (
                        'Create Channel'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )

      case 'mcp':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">MCP Server</h3>
              <p className="text-sm text-gray-500 mt-1">
                Connect external AI systems to your organization using the Model Context Protocol (MCP).
              </p>
            </div>

            <div className="bg-white border-2 border-[#1a1a2e] rounded-xl shadow-[2px_2px_0px_#1a1a2e] p-5">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Server Endpoint</h4>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-800">
                  https://api.chatcup.ai/mcp
                </code>
                <button
                  onClick={() => { navigator.clipboard.writeText('https://api.chatcup.ai/mcp'); toast.success('Copied!') }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">JSON-RPC 2.0 over HTTP — send POST requests to this endpoint.</p>
            </div>

            <div className="bg-white border-2 border-[#1a1a2e] rounded-xl shadow-[2px_2px_0px_#1a1a2e] p-5">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Discovery URL</h4>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-800">
                  https://api.chatcup.ai/mcp/.well-known/mcp
                </code>
                <button
                  onClick={() => { navigator.clipboard.writeText('https://api.chatcup.ai/mcp/.well-known/mcp'); toast.success('Copied!') }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">Auto-discovery endpoint for MCP-compatible clients.</p>
            </div>

            <div className="bg-white border-2 border-[#1a1a2e] rounded-xl shadow-[2px_2px_0px_#1a1a2e] p-5">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Authentication</h4>
              <p className="text-sm text-gray-600 mb-3">
                Use an API token from the <button onClick={() => setActiveTab('api-tokens')} className="text-violet-600 hover:underline font-medium">API Tokens</button> tab as a Bearer token.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap">{`Authorization: Bearer sk_live_your_token_here`}</pre>
              </div>
            </div>

            <div className="bg-white border-2 border-[#1a1a2e] rounded-xl shadow-[2px_2px_0px_#1a1a2e] p-5">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Quick Start</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">1. Initialize</p>
                  <div className="bg-gray-900 rounded-lg p-3">
                    <pre className="text-xs font-mono text-gray-100 overflow-x-auto">{`curl -X POST https://api.chatcup.ai/mcp \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"initialize","params":{}}'`}</pre>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">2. List available tools</p>
                  <div className="bg-gray-900 rounded-lg p-3">
                    <pre className="text-xs font-mono text-gray-100 overflow-x-auto">{`curl -X POST https://api.chatcup.ai/mcp \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":2,"method":"tools/list","params":{}}'`}</pre>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">3. Call a tool</p>
                  <div className="bg-gray-900 rounded-lg p-3">
                    <pre className="text-xs font-mono text-gray-100 overflow-x-auto">{`curl -X POST https://api.chatcup.ai/mcp \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":3,"method":"tools/call",
       "params":{"name":"list_agents","arguments":{}}}'`}</pre>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-[#1a1a2e] rounded-xl shadow-[2px_2px_0px_#1a1a2e] p-5">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Supported Methods</h4>
              <div className="space-y-2">
                {[
                  { method: 'initialize', desc: 'Initialize the MCP session' },
                  { method: 'tools/list', desc: 'List all available tools and their schemas' },
                  { method: 'tools/call', desc: 'Execute a tool with parameters' },
                ].map((m) => (
                  <div key={m.method} className="flex items-center gap-3 text-sm">
                    <code className="px-2 py-0.5 bg-violet-50 text-violet-700 rounded font-mono text-xs">{m.method}</code>
                    <span className="text-gray-600">{m.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )

      case 'referrals':
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Your Referral Programs</h2>
            {referralLoading ? (
              <p className="text-gray-500 text-sm">Loading...</p>
            ) : referralCodes.length === 0 ? (
              <p className="text-gray-500 text-sm">No active referral programs available.</p>
            ) : (
              referralCodes.map(code => (
                <div key={code.id} className="bg-white rounded-xl border-2 border-[#1a1a2e] p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">{code.campaign_name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${code.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {code.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Code: </span>
                      <span className="font-mono">{code.code}</span>
                      <button onClick={() => copyToClipboard(code.code)} className="ml-2 text-blue-500 text-xs hover:underline">Copy</button>
                    </div>
                    <div>
                      <span className="text-gray-500">Link: </span>
                      <span className="font-mono text-xs truncate">https://chatcup.ai/ref/{code.slug}</span>
                      <button onClick={() => copyToClipboard(`https://chatcup.ai/ref/${code.slug}`)} className="ml-2 text-blue-500 text-xs hover:underline">Copy</button>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    Reward: {code.reward_type === 'credits'
                      ? `$${code.reward_value} credits per referral`
                      : `${code.reward_value}${code.campaign_discount_type === 'percentage' ? '%' : '$'} off subscription per referral`}
                  </div>
                </div>
              ))
            )}
          </div>
        )

      case 'webhooks':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Webhooks</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Receive real-time event notifications when customers interact with your AI agents.
                </p>
              </div>
              <button
                onClick={() => { resetWebhookForm(); setShowCreateWebhookModal(true) }}
                className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Webhook
              </button>
            </div>

            {/* Secret display after creation */}
            {createdWebhookSecret && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-yellow-800">Webhook Secret — Save this now!</p>
                    <p className="text-xs text-yellow-600 mt-1">This secret won't be shown again. Use it to verify webhook signatures.</p>
                    <div className="mt-2 flex items-center gap-2">
                      <code className="flex-1 bg-yellow-100 px-3 py-2 rounded text-sm font-mono text-yellow-900 break-all">
                        {createdWebhookSecret}
                      </code>
                      <button
                        onClick={() => { navigator.clipboard.writeText(createdWebhookSecret); toast.success('Secret copied') }}
                        className="p-2 hover:bg-yellow-100 rounded"
                      >
                        <Copy className="w-4 h-4 text-yellow-700" />
                      </button>
                    </div>
                    <button
                      onClick={() => { setCreatedWebhookSecret(null); setShowCreateWebhookModal(false) }}
                      className="mt-3 text-sm text-yellow-700 hover:text-yellow-900 font-medium"
                    >
                      I've saved the secret, close this
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isLoadingWebhooks ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading webhooks...</span>
              </div>
            ) : webhooks.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Link2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No webhooks configured</p>
                <p className="text-sm mt-1">Create a webhook to receive real-time event notifications.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {webhooks.map((webhook) => (
                  <div key={webhook.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`inline-block w-2 h-2 rounded-full ${webhook.is_active ? 'bg-green-400' : 'bg-gray-300'}`} />
                            <code className="text-sm font-mono text-gray-900 truncate">{webhook.url}</code>
                          </div>
                          {webhook.description && (
                            <p className="text-sm text-gray-500 mt-1">{webhook.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {webhook.events.map((event) => (
                              <span key={event} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                {event}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-4 shrink-0">
                          <button
                            onClick={() => handleToggleWebhook(webhook)}
                            className={`p-1.5 rounded hover:bg-gray-100 ${webhook.is_active ? 'text-green-600' : 'text-gray-400'}`}
                            title={webhook.is_active ? 'Disable' : 'Enable'}
                          >
                            {webhook.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => {
                              setEditingWebhook(webhook)
                              setWebhookUrl(webhook.url)
                              setWebhookDescription(webhook.description || '')
                              setWebhookEvents(webhook.events)
                              setShowCreateWebhookModal(true)
                            }}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleTestWebhook(webhook.id)}
                            disabled={testingWebhookId === webhook.id || !webhook.is_active}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-50"
                            title="Send test event"
                          >
                            {testingWebhookId === webhook.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleViewLogs(webhook.id)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                            title="View delivery logs"
                          >
                            {expandedWebhookLogs === webhook.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleDeleteWebhook(webhook.id)}
                            className="p-1.5 rounded hover:bg-gray-100 text-red-500"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Delivery logs */}
                    {expandedWebhookLogs === webhook.id && (
                      <div className="border-t border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-700">Recent Deliveries</h4>
                          <button
                            onClick={async () => {
                              setIsLoadingLogs(true)
                              try {
                                const result = await webhooksService.getDeliveryLogs(webhook.id)
                                setWebhookLogs(prev => ({ ...prev, [webhook.id]: result.logs }))
                              } catch { /* ignore */ } finally {
                                setIsLoadingLogs(false)
                              }
                            }}
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Refresh
                          </button>
                        </div>
                        {isLoadingLogs ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                          </div>
                        ) : !webhookLogs[webhook.id]?.length ? (
                          <p className="text-sm text-gray-500 text-center py-4">No deliveries yet</p>
                        ) : (
                          <div className="space-y-2">
                            {webhookLogs[webhook.id].map((log) => (
                              <div key={log.id} className="bg-white rounded border border-gray-200 px-3 py-2 text-sm">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                                      log.status === 'success' ? 'bg-green-400' :
                                      log.status === 'failed' ? 'bg-red-400' : 'bg-yellow-400'
                                    }`} />
                                    <span className="font-medium text-gray-700">{log.event_type}</span>
                                    {log.http_status && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        log.http_status >= 200 && log.http_status < 300 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                                      }`}>
                                        {log.http_status}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-400">
                                    {new Date(log.created_at).toLocaleString()}
                                  </span>
                                </div>
                                {log.error && (
                                  <p className="text-xs text-red-500 mt-1">{log.error}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Create/Edit Webhook Modal */}
            {showCreateWebhookModal && !createdWebhookSecret && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {editingWebhook ? 'Edit Webhook' : 'Create Webhook'}
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL</label>
                        <input
                          type="url"
                          value={webhookUrl}
                          onChange={(e) => setWebhookUrl(e.target.value)}
                          placeholder="https://your-server.com/webhook"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                        <input
                          type="text"
                          value={webhookDescription}
                          onChange={(e) => setWebhookDescription(e.target.value)}
                          placeholder="e.g., Production CRM integration"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Events to subscribe</label>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {WEBHOOK_EVENT_OPTIONS.map((opt) => (
                            <label key={opt.value} className="flex items-start gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={webhookEvents.includes(opt.value)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setWebhookEvents(prev => [...prev, opt.value])
                                  } else {
                                    setWebhookEvents(prev => prev.filter(v => v !== opt.value))
                                  }
                                }}
                                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div>
                                <span className="text-sm font-medium text-gray-700">{opt.label}</span>
                                <p className="text-xs text-gray-500">{opt.description}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                      <button
                        onClick={() => { setShowCreateWebhookModal(false); resetWebhookForm() }}
                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={editingWebhook ? handleUpdateWebhook : handleCreateWebhook}
                        disabled={isSavingWebhook}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
                      >
                        {isSavingWebhook && <Loader2 className="w-4 h-4 animate-spin" />}
                        {editingWebhook ? 'Save Changes' : 'Create Webhook'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )

      case 'nikaido': {
        const nikPending =
          !nikaidoStatus?.connected &&
          !!nikaidoStatus?.has_credentials &&
          (nikaidoStatus?.status === 'pending' || nikaidoStatus?.callback_configured === false)
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Nikaido Connection</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Connect this organization to the workflow editor used by ChatCup agents.
                </p>
              </div>
              <button
                onClick={fetchNikaidoStatus}
                disabled={isLoadingNikaido}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingNikaido ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {isLoadingNikaido ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading Nikaido connection...</span>
              </div>
            ) : (
              <>
                <div className="border-2 border-[#1a1a2e] rounded-xl p-5 bg-white">
                  {/* Plan 010 — a provisioned org whose callback-config isn't wired
                      is "pending" (has credentials but replies can't deliver): amber
                      state, not "Connected". `nikPending` is computed at case scope. */}
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        nikaidoStatus?.connected ? 'bg-green-50 text-green-600' : nikPending ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {nikaidoStatus?.connected ? <CheckCircle2 className="w-5 h-5" /> : nikPending ? <AlertCircle className="w-5 h-5" /> : <Workflow className="w-5 h-5" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-gray-900">
                            {nikaidoStatus?.connected ? 'Connected' : nikPending ? 'Pending — callback not configured' : 'Not connected'}
                          </h4>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            nikaidoStatus?.connected ? 'bg-green-50 text-green-700' : nikPending ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {nikaidoStatus?.status || (nikaidoStatus?.connected ? 'connected' : 'inactive')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {nikaidoStatus?.connected
                            ? 'This organization can use embedded Nikaido workflow pages.'
                            : nikPending
                              ? (nikaidoStatus?.error_message || 'Provisioned, but the Nikaido callback-config is not set. Configure an auth:none callback to /api/webhooks/callback before replies can reach ChatCup.')
                              : 'Create or connect a Nikaido organization before attaching workflows to agents.'}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {!nikaidoStatus?.connected && !nikPending && (
                        <button
                          onClick={handleProvisionNikaido}
                          disabled={isProvisioningNikaido}
                          className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors text-sm font-medium"
                        >
                          {isProvisioningNikaido ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Plus className="w-4 h-4 mr-2" />
                          )}
                          Create connection
                        </button>
                      )}
                      <button
                        onClick={() => setShowManualNikaido((prev) => !prev)}
                        className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                      >
                        <Key className="w-4 h-4 mr-2" />
                        {showManualNikaido ? 'Hide manual setup' : 'Manual token'}
                      </button>
                      {(nikaidoStatus?.connected || nikPending) && (
                        <button
                          onClick={handleDisconnectNikaido}
                          disabled={isSavingNikaido}
                          className="inline-flex items-center px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors text-sm font-medium"
                        >
                          {isSavingNikaido ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                          Disconnect
                        </button>
                      )}
                    </div>
                  </div>

                  {(nikaidoStatus?.connected || nikPending) && (
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Nikaido Org ID</label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 break-all">
                            {nikaidoStatus.org_id}
                          </code>
                          {nikaidoStatus.org_id && (
                            <button
                              onClick={() => copyToClipboard(nikaidoStatus.org_id!)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Copy org ID"
                            >
                              <Copy className="w-4 h-4 text-gray-500" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Token</label>
                        <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-800">
                          {nikaidoStatus.token_masked || 'af_****'}
                        </code>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">API URL</label>
                        <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 break-all">
                          {nikaidoStatus.api_base_url}
                        </code>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Web URL</label>
                        <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 break-all">
                          {nikaidoStatus.web_base_url}
                        </code>
                      </div>
                      {nikaidoStatus.connected_at && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Connected</label>
                          <p className="text-sm text-gray-700">{new Date(nikaidoStatus.connected_at).toLocaleString()}</p>
                        </div>
                      )}
                      {nikaidoStatus.last_verified_at && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Last Verified</label>
                          <p className="text-sm text-gray-700">{new Date(nikaidoStatus.last_verified_at).toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {nikaidoStatus?.error_message && (
                    <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3">
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                      <p className="text-sm text-red-700">{nikaidoStatus.error_message}</p>
                    </div>
                  )}
                </div>

                {showManualNikaido && (
                  <div className="border-2 border-[#1a1a2e] rounded-xl p-5 bg-white">
                    <h4 className="font-semibold text-gray-900 mb-4">Manual Token Connection</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nikaido Token</label>
                        <input
                          type="password"
                          value={nikaidoToken}
                          onChange={(e) => setNikaidoToken(e.target.value)}
                          placeholder="af_xxx"
                          className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">API Base URL</label>
                        <input
                          type="url"
                          value={nikaidoApiBaseUrl}
                          onChange={(e) => setNikaidoApiBaseUrl(e.target.value)}
                          placeholder="https://nikaido-api.aunto.workers.dev"
                          className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Web Base URL</label>
                        <input
                          type="url"
                          value={nikaidoWebBaseUrl}
                          onChange={(e) => setNikaidoWebBaseUrl(e.target.value)}
                          placeholder="https://nikaido-web.pages.dev"
                          className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div className="mt-5 flex justify-end">
                      <button
                        onClick={handleConnectNikaido}
                        disabled={isSavingNikaido}
                        className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors text-sm font-medium"
                      >
                        {isSavingNikaido ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
                        Connect token
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )
      }

      case 'security':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Change Password</h3>
              <p className="text-sm text-gray-500 mb-6">Update your password to keep your account secure.</p>
              <div className="max-w-md space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="w-full px-3 py-2 pr-10 border-2 border-[#1a1a2e] rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] focus:shadow-[3px_3px_0px_#1a1a2e] outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 8 characters)"
                      className="w-full px-3 py-2 pr-10 border-2 border-[#1a1a2e] rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] focus:shadow-[3px_3px_0px_#1a1a2e] outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {newPassword.length > 0 && newPassword.length < 8 && (
                    <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="w-full px-3 py-2 pr-10 border-2 border-[#1a1a2e] rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] focus:shadow-[3px_3px_0px_#1a1a2e] outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && confirmPassword !== newPassword && (
                    <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                  )}
                </div>
                <div className="pt-2">
                  <button
                    onClick={handleChangePassword}
                    disabled={isChangingPassword || !currentPassword || newPassword.length < 8 || newPassword !== confirmPassword}
                    className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-xl text-sm font-bold transition-all border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] hover:shadow-[3px_3px_0px_#1a1a2e] hover:translate-x-[-1px] hover:translate-y-[-1px] active:shadow-[1px_1px_0px_#1a1a2e] active:translate-x-[1px] active:translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isChangingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">
          Manage your account and preferences
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        <div className="w-full md:w-64 shrink-0">
          <nav className="bg-white border-2 border-[#1a1a2e] rounded-lg p-2 space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-left transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="bg-white border-2 border-[#1a1a2e] rounded-lg p-6">
            {renderTabContent()}
            {activeTab !== 'api-tokens' && activeTab !== 'ai-providers' && activeTab !== 'notification-channels' && activeTab !== 'mcp' && activeTab !== 'referrals' && activeTab !== 'webhooks' && activeTab !== 'nikaido' && activeTab !== 'security' && (
              <div className="mt-6 pt-6 border-t border-[#1a1a2e] flex justify-end">
                <button className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                  <Save className="w-5 h-5 mr-2" />
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsPage
